import styled from 'styled-components';
import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains/foldering';
import { SearchableText } from 'ui-beta/searchableText';
import { ChevronDownSmall } from 'ui/icons/navigation/ChevronDownSmall';
import { Tooltip } from 'ui/tooltip/Tooltip';
import { colors, spacing } from 'ui/theme';
import { TreeFolderIcon } from './TreeFolderIcon';

export type Props = {
  ancestors?: (AncestorIdData & AncestorNameData)[];
  /** the ID of the folder */
  id: FolderId;
  /** is the folder a valid target */
  isDisabled: boolean;
  /** is the folder open (showing children) */
  isOpen: boolean;
  /** is the folder selected as the target */
  isSelected: boolean;
  /** the name of the folder, useful to avoid lag in fetching data */
  name: string;
  /** currently entered text to highlight */
  searchText: string;
  /** function to open/close the folder */
  setIsOpen: (newState: boolean) => void;
  /** update the selected folder id to a new value */
  setSelectedFolder: (
    folderId: FolderId,
    name: string,
    ancestors?: (AncestorIdData & AncestorNameData)[],
  ) => void;
};

const RotateChevron = styled(ChevronDownSmall)`
  transform: rotate(${({ $isOpen }: { $isOpen: boolean }) => ($isOpen ? '0deg' : '-90deg')});
`;

const Label = styled.div`
  color: ${({ $isDisabled }: { $isDisabled: boolean }) =>
    $isDisabled ? colors.grey25 : 'inherit'};
`;

type StyledFolderLabelProps = {
  isSelected: boolean;
};

const StyledFolderLabel = styled.div`
  align-items: center;
  background: ${({ isSelected }: StyledFolderLabelProps) =>
    isSelected ? colors.cyan10 : 'transparent'};
  column-gap: ${spacing.base};
  display: grid;
  grid-template-columns: auto auto 1fr;
  padding: ${spacing.half} ${spacing.base};
`;

export const TreeFolderLabel = ({
  ancestors = [],
  id,
  isDisabled = false,
  isOpen = false,
  isSelected = false,
  name = '',
  searchText = '',
  setIsOpen,
  setSelectedFolder,
}: Props) => {
  const handleClick = (newOpenState?: boolean): void => {
    setIsOpen(newOpenState ?? !isOpen);
  };

  return (
    <StyledFolderLabel
      aria-expanded={isOpen ? 'true' : 'false'}
      data-test={`folder-label-${id}`}
      isSelected={isSelected}
      onClick={() => {
        if (!isDisabled) {
          handleClick(!isOpen);
          setSelectedFolder(id, name, ancestors);
        }
      }}
      role="treeitem"
      tabIndex={0}
    >
      <RotateChevron
        color={isDisabled ? colors.grey25 : undefined}
        data-test="folder-status-chevron"
        $isOpen={isOpen}
      />
      <TreeFolderIcon isDisabled={isDisabled} isOpen={isOpen} />

      {isDisabled ? (
        <Label data-test="disabled-text-container" $isDisabled={isDisabled}>
          <Tooltip tooltipContent="The folder can't be moved into itself.">{name}</Tooltip>
        </Label>
      ) : (
        <div data-test="searchable-text-container">
          <SearchableText isCaseSensitive={false} searchText={searchText} text={name} />
        </div>
      )}
    </StyledFolderLabel>
  );
};
